/* eslint-disable react/prop-types */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import buildRequestUrl from "lib/requests/requestUtils";
import athenaClient from "lib/axios/clients";
import { useEffectAsync } from "lib/react/customHooks";
import { makeStyles, Typography } from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import { GoLocation } from "react-icons/go";
import { AiOutlineShop, AiOutlineTags, AiOutlineTag } from "react-icons/ai";
import CardComponent from "app/common/CardComponent";
import ElasticityBubbleMapChartComponent from "./ElasticityBubbleMapChartComponent";
import ExplicabilityComponent from "../../explicability-info/ExplicabilityComponent";
import SelectComponent from "../SelectComponent";
import { setTupleData } from "../duck/slices";

const useStyles = makeStyles((theme) => ({
  aggregateContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "5px",
  },
  cardRow: {
    display: "flex",
    marginBottom: "15px",
    justifyContent: "space-around",
    alignItems: "center",
  },
  cardRowItem: {
    flex: "50%",
  },
  secondColumn: {
    paddingLeft: "10px",
  },
  scopeTitle: {
    color: "#a0a0a0",
  },
  chartContainer: {
    position: "relative",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("sm")]: {
      height: "200px",
    },
    [theme.breakpoints.up("md")]: {
      height: "300px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
  explicabilityContainer: {
    marginLeft: "-12rem",
    marginTop: "-0.12rem",
  },
}));

function ElasticityBubbleMapComponent({ filtersKey, queryFilters }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersSlice = useSelector((state) => state.filters);
  const elasticitiesSlice = useSelector((state) => state.elasticities);
  const catalogSlice = useSelector((state) => state.catalog);
  const retailersSlice = useSelector((state) => state.retailers);
  const retailersGroupSlice = useSelector((state) => state.retailersGroup);
  const areasSlice = useSelector((state) => state.areas);

  const explicabilityTooltipText = (
    <div style={{ whiteSpace: "pre-line" }}>
      {
        "X axis: Own elasticity of demand \n Y axis: Volume \n Bubble size: Margin \n\n The further to the right the more elastic the bubble is. A bubble is a product by default, but you can customize it using the drop down list."
      }
    </div>
  );

  const [elasticityData, setElasticityData] = React.useState(null);
  const [timeSeriesGroupBy, setTimeSeriesGroupBy] = React.useState({
    key: "product_id",
    label: "By Product",
    icon: <AiOutlineTag size={22} />,
  });

  const selectOptions = [
    {
      key: "category_id",
      label: "By Category",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "line_id",
      label: "By Line",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "brand_id",
      label: "By Brand",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "product_id",
      label: "By Product",
      icon: <AiOutlineTag size={22} />,
    },
    {
      key: "retailer_group_id",
      label: "By Retailer Group",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "retailer_id",
      label: "By Retailer",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "area_id",
      label: "By Area",
      icon: <GoLocation size={22} />,
    },
  ];

  const retrieveElasticityForTuple = async (tupleKey) => {
    if (
      elasticitiesSlice.tuples[tupleKey] &&
      elasticitiesSlice.tuples[tupleKey].elasticities
    ) {
      return {
        key: tupleKey,
        elasticities: elasticitiesSlice.tuples[tupleKey].elasticities,
        minRange: elasticitiesSlice.tuples[tupleKey].minRange,
        maxRange: elasticitiesSlice.tuples[tupleKey].maxRange,
      };
    }

    const requestQueryFilters = {
      ...queryFilters,
      is_product_client: 1,
      data_source_id: 1,
    };

    const elasticitiesUrl = buildRequestUrl(
      {
        filters: requestQueryFilters,
        group_by: [timeSeriesGroupBy.key, "date"],
        avg_weight_by_pred_units_last_price: ["elasticity"],
        sum: [
          "pred_units_last_price",
          "pred_margin_last_price",
          "pred_sales_last_price",
        ],
        join_by: "target",
        filter_type: "same",
        min_date: moment()
          .startOf("month")
          .format("YYYY-MM-DD"),
        max_date: moment()
          .add(1, "years")
          .endOf("month")
          .format("YYYY-MM-DD"),
      },
      "elasticities"
    );

    const elasticitiesConfig = {
      method: "GET",
      url: elasticitiesUrl,
    };

    const elasticityResult = await athenaClient(elasticitiesConfig);

    let elasticities = _.orderBy(elasticityResult.data, "date", "asc");

    elasticities = _(elasticities)
      .groupBy(timeSeriesGroupBy.key)
      .map((elasticity, id) => ({
        [timeSeriesGroupBy.key]: Number(id),
        sum_pred_margin_last_price: _.some(
          elasticity,
          (item) => item.sum_pred_margin_last_price !== null
        )
          ? _.sumBy(elasticity, "sum_pred_margin_last_price")
          : null,
        sum_pred_sales_last_price: _.some(
          elasticity,
          (item) => item.sum_pred_sales_last_price !== null
        )
          ? _.sumBy(elasticity, "sum_pred_sales_last_price")
          : null,
        sum_pred_units_last_price: _.some(
          elasticity,
          (item) => item.sum_pred_units_last_price !== null
        )
          ? _.sumBy(elasticity, "sum_pred_units_last_price")
          : null,
        avg_weight_by_pred_units_last_price_elasticity: _.some(
          elasticity,
          (item) => item.avg_weight_by_pred_units_last_price_elasticity !== null
        )
          ? _.sumBy(
              elasticity,
              (item) =>
                item.avg_weight_by_pred_units_last_price_elasticity *
                item.sum_pred_units_last_price
            ) / _.sumBy(elasticity, "sum_pred_units_last_price")
          : null,
      }))
      .value();

    const minRange = _.min(
      _.map(elasticities, (elasticity) => elasticity.sum_pred_margin_last_price)
    );
    const maxRange = _.max(
      _.map(elasticities, (elasticity) => elasticity.sum_pred_margin_last_price)
    );

    elasticities = _.groupBy(elasticities, timeSeriesGroupBy.key);

    // Due to null values in database sometimes we may end up with a NaN key that we have to remove
    delete elasticities.NaN;

    return {
      key: tupleKey,
      elasticities,
      minRange,
      maxRange,
    };
  };

  const getLabelForGroupBy = (itemId) => {
    if (itemId === "null" || itemId === null || Number.isNaN(itemId)) return ""; // There are null values coming from the database and the lodash groupBy places them into the "null" property
    switch (timeSeriesGroupBy.key) {
      case "retailer_id":
        return `${retailersSlice[itemId].label}`;
      case "area_id":
        return `${areasSlice[itemId].label}`;
      case "brand_id":
        return `${catalogSlice.brands[itemId].label}`;
      case "line_id":
        return `${catalogSlice.lines[itemId].label}`;
      case "category_id":
        return `${catalogSlice.categories[itemId].label}`;
      case "retailer_group_id":
        return `${retailersGroupSlice[itemId].label}`;
      default:
        return `${catalogSlice.products[itemId].label}`;
    }
  };

  const getDatasetFromElasticityTimeSeries = (
    elasticityTimeSeries,
    timeSeriesCatalogItemId,
    minRange,
    maxRange
  ) => {
    const radius =
      elasticityTimeSeries.sum_pred_margin_last_price - minRange === 0
        ? 5
        : 5 +
          ((elasticityTimeSeries.sum_pred_margin_last_price - minRange) *
            (20 - 5)) /
            (maxRange - minRange);

    return {
      label: `Elasticity-${timeSeriesCatalogItemId}`,
      yAxisID: "elasticity",
      type: "bubble",
      fill: false,
      borderColor: "rgba(165, 21, 87, 0.3)",
      pointBordercolor: "rgba(165, 21, 87, 0.3)",
      pointBackgroundColor: "#ffffff",
      backgroundColor: "rgba(165, 21, 87, 0.3)",
      hoverBorderWidth: 3,
      hoverBorderColor: "#A51557",
      hoverBackgroundColor: "#A51557",
      pointHoverBackgroundColor: "#A51557",
      data: [
        {
          ...elasticityTimeSeries,
          x:
            elasticityTimeSeries.avg_weight_by_pred_units_last_price_elasticity,
          y: elasticityTimeSeries.sum_pred_units_last_price,
          r: radius,
          label: `${getLabelForGroupBy(timeSeriesCatalogItemId)}`,
          avg_weight_by_pred_units_last_price_elasticity: {
            absolute:
              elasticityTimeSeries.avg_weight_by_pred_units_last_price_elasticity,
          },
          sum_pred_units_last_price: {
            absolute: elasticityTimeSeries.sum_pred_units_last_price,
          },
          sum_pred_margin_last_price: {
            absolute: elasticityTimeSeries.sum_pred_margin_last_price,
          },
          sum_pred_sales_last_price: {
            absolute: elasticityTimeSeries.sum_pred_sales_last_price,
          },
        },
      ],
    };
  };

  useEffectAsync(
    async () => {
      const tupleKey = `bubblemap-${filtersKey}-${timeSeriesGroupBy.key}`;

      return retrieveElasticityForTuple(tupleKey);
    },
    (result) => {
      dispatch(
        setTupleData({
          [result.key]: {
            elasticities: result.elasticities,
            minRange: result.minRange,
            maxRange: result.maxRange,
          },
        })
      );

      setElasticityData({
        datasets: _.map(result.elasticities, (elasticityTimeSeries) => {
          return getDatasetFromElasticityTimeSeries(
            elasticityTimeSeries[0],
            elasticityTimeSeries[0][timeSeriesGroupBy.key],
            result.minRange,
            result.maxRange
          );
        }),
      });
    },
    [filtersSlice.filters, timeSeriesGroupBy.key]
  );

  return (
    <>
      {elasticityData && (
        <CardComponent
          title={
            <>
              <Typography variant="body1" className={classes.componentTitle}>
                Own Elasticity Grid
                <ExplicabilityComponent
                  tooltipText={explicabilityTooltipText}
                />
              </Typography>

              <div className={classes.aggregateContainer}>
                <SelectComponent
                  selectValue={timeSeriesGroupBy}
                  setSelectValue={setTimeSeriesGroupBy}
                  selectTitle="Aggregation"
                  selectOptions={selectOptions}
                />
              </div>
            </>
          }
          body={
            <div className={classes.chartContainer}>
              <ElasticityBubbleMapChartComponent
                groupBy={timeSeriesGroupBy}
                setGroupBy={setTimeSeriesGroupBy}
                data={elasticityData}
              />
            </div>
          }
        />
      )}
    </>
  );
}

export default ElasticityBubbleMapComponent;
